import { useState } from 'react';
import { X, Loader2, CheckCircle2, Clock, Minus, Plus, Truck, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import { api } from '../lib/api';
import type { Job } from '../lib/store';

export type NextStatus = 'Ready to Invoice' | 'Awaiting Parts' | 'Follow-up Visit Required';

const VARIANTS: Record<
  NextStatus,
  {
    title: string;
    subtitle: string;
    hoursLabel: string;
    confirmLabel: string;
    buttonClass: string;
    icon: any;
    iconBoxClass: string;
    iconColorClass: string;
    totalBoxClass: string;
    totalLabelClass: string;
    totalValueClass: string;
    totalSubClass: string;
  }
> = {
  'Ready to Invoice': {
    title: 'Finish job & send to Sammy',
    subtitle: 'Confirms hours and adds this job to the invoicing queue.',
    hoursLabel: 'Hours worked',
    confirmLabel: 'Finish · Send for invoicing',
    buttonClass: 'bg-emerald-600 text-white hover:bg-emerald-700',
    icon: CheckCircle2,
    iconBoxClass: 'bg-emerald-50',
    iconColorClass: 'text-emerald-600',
    totalBoxClass: 'bg-emerald-50 border-emerald-200',
    totalLabelClass: 'text-emerald-800',
    totalValueClass: 'text-emerald-900',
    totalSubClass: 'text-emerald-700',
  },
  'Awaiting Parts': {
    title: 'Log hours & pause for parts',
    subtitle: 'Job will pause; you can resume anytime.',
    hoursLabel: 'Hours worked so far',
    confirmLabel: 'Save & pause job',
    buttonClass: 'bg-orange-600 text-white hover:bg-orange-700',
    icon: Truck,
    iconBoxClass: 'bg-orange-50',
    iconColorClass: 'text-orange-600',
    totalBoxClass: 'bg-orange-50 border-orange-200',
    totalLabelClass: 'text-orange-800',
    totalValueClass: 'text-orange-900',
    totalSubClass: 'text-orange-700',
  },
  'Follow-up Visit Required': {
    title: 'Log hours & flag follow-up',
    subtitle: 'Book the return visit now or let Sammy schedule.',
    hoursLabel: 'Hours worked so far',
    confirmLabel: 'Save & continue',
    buttonClass: 'bg-yellow-500 text-white hover:bg-yellow-600',
    icon: RefreshCw,
    iconBoxClass: 'bg-yellow-50',
    iconColorClass: 'text-yellow-600',
    totalBoxClass: 'bg-yellow-50 border-yellow-200',
    totalLabelClass: 'text-yellow-800',
    totalValueClass: 'text-yellow-900',
    totalSubClass: 'text-yellow-700',
  },
};

export default function ConfirmHoursModal({
  job,
  nextStatus = 'Ready to Invoice',
  onClose,
  onConfirmed,
}: {
  job: Job;
  nextStatus?: NextStatus;
  onClose: () => void;
  onConfirmed: (opts: { openFollowUp: boolean }) => void;
}) {
  const variant = VARIANTS[nextStatus];
  const estimateHours =
    job.duration_type === 'hours' ? job.duration_hours || 1 : (job.duration_days || 1) * 8;
  const previouslyLogged = job.hours_to_invoice || 0;

  const [hoursThisVisit, setHoursThisVisit] = useState<number>(
    nextStatus === 'Ready to Invoice' ? estimateHours : Math.min(estimateHours, 1)
  );
  const [addMode, setAddMode] = useState<boolean>(previouslyLogged > 0);
  const [scheduleNow, setScheduleNow] = useState<boolean>(false);
  const [saving, setSaving] = useState(false);

  const finalTotal = addMode ? previouslyLogged + hoursThisVisit : hoursThisVisit;
  const labourRate = 52.19;
  const estimatedInvoice = finalTotal * labourRate;

  const bump = (delta: number) => {
    setHoursThisVisit((h) => Math.max(0, Math.round((h + delta) * 4) / 4));
  };

  const confirm = async () => {
    setSaving(true);
    try {
      const patch: any = { status: nextStatus };
      // Only send hours if > 0
      if (finalTotal > 0) patch.hours_to_invoice = finalTotal;
      await api.put(`/jobs/${job.id}`, patch);
      const suffix =
        finalTotal > 0
          ? ` · ${finalTotal}h logged`
          : '';
      toast.success(`Status: ${nextStatus}${suffix}`);
      onConfirmed({ openFollowUp: nextStatus === 'Follow-up Visit Required' && scheduleNow });
      onClose();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const Icon = variant.icon;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md bg-white rounded-2xl shadow-pop overflow-hidden">
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl ${variant.iconBoxClass} grid place-items-center`}>
              <Icon className={`h-5 w-5 ${variant.iconColorClass}`} />
            </div>
            <div>
              <h2 className="font-bold text-slate-900">{variant.title}</h2>
              <p className="text-xs text-slate-500">
                {job.job_number} · {variant.subtitle}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100">
            <X className="h-5 w-5 text-slate-500" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {previouslyLogged > 0 && (
            <div className="rounded-lg bg-slate-50 border border-slate-100 p-3 flex items-center justify-between text-sm">
              <div className="text-slate-600">Already logged on previous visits</div>
              <div className="font-mono font-bold text-slate-900">{previouslyLogged}h</div>
            </div>
          )}

          <div>
            <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2 block">
              {variant.hoursLabel}
            </label>
            <div className="flex items-center gap-3 bg-slate-50 rounded-xl p-2">
              <button
                onClick={() => bump(-0.25)}
                type="button"
                className="w-11 h-11 rounded-lg bg-white border border-slate-200 grid place-items-center hover:bg-slate-100 shrink-0"
              >
                <Minus className="h-4 w-4 text-slate-600" />
              </button>
              <input
                type="number"
                min={0}
                step={0.25}
                value={hoursThisVisit}
                onChange={(e) => setHoursThisVisit(Number(e.target.value))}
                className="flex-1 text-center text-4xl font-bold text-slate-900 bg-transparent outline-none tabular-nums"
              />
              <button
                onClick={() => bump(0.25)}
                type="button"
                className="w-11 h-11 rounded-lg bg-white border border-slate-200 grid place-items-center hover:bg-slate-100 shrink-0"
              >
                <Plus className="h-4 w-4 text-slate-600" />
              </button>
            </div>
            <div className="flex justify-center gap-2 mt-2 flex-wrap">
              {[0.25, 0.5, 1, 1.5, 2, 3, 4].map((h) => (
                <button
                  key={h}
                  onClick={() => setHoursThisVisit(h)}
                  className={`px-2.5 py-1 rounded-md text-xs font-semibold ${
                    hoursThisVisit === h
                      ? 'bg-brand-600 text-white'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  {h}h
                </button>
              ))}
            </div>
            <p className="text-[11px] text-slate-400 mt-2">
              Estimate for this job was {estimateHours}h
            </p>
          </div>

          {previouslyLogged > 0 && (
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={addMode}
                onChange={(e) => setAddMode(e.target.checked)}
                className="rounded"
              />
              <span className="text-slate-700">Add to previously-logged hours</span>
            </label>
          )}

          {/* Follow-up scheduling toggle */}
          {nextStatus === 'Follow-up Visit Required' && (
            <div className="rounded-xl border border-yellow-200 bg-yellow-50/60 p-3">
              <div className="text-xs font-bold text-yellow-800 uppercase tracking-wider mb-2">
                Follow-up visit
              </div>
              <div className="space-y-2">
                <label className="flex items-start gap-2 text-sm cursor-pointer">
                  <input
                    type="radio"
                    checked={!scheduleNow}
                    onChange={() => setScheduleNow(false)}
                    className="mt-0.5"
                  />
                  <div>
                    <div className="font-semibold text-slate-900">Sammy will schedule later</div>
                    <div className="text-xs text-slate-500">
                      Flags this job as needing follow-up. No date required now.
                    </div>
                  </div>
                </label>
                <label className="flex items-start gap-2 text-sm cursor-pointer">
                  <input
                    type="radio"
                    checked={scheduleNow}
                    onChange={() => setScheduleNow(true)}
                    className="mt-0.5"
                  />
                  <div>
                    <div className="font-semibold text-slate-900">Book the follow-up now</div>
                    <div className="text-xs text-slate-500">
                      Pick date/time on the next screen (AI will suggest a reason).
                    </div>
                  </div>
                </label>
              </div>
            </div>
          )}

          {finalTotal > 0 && (
            <div className={`rounded-xl border p-3 ${variant.totalBoxClass}`}>
              <div className="flex items-center justify-between mb-1">
                <span className={`text-xs font-bold uppercase tracking-wider ${variant.totalLabelClass}`}>
                  {nextStatus === 'Ready to Invoice' ? 'Total billable' : 'Hours logged'}
                </span>
                <span className={`font-mono text-lg font-bold ${variant.totalValueClass}`}>
                  {finalTotal}h
                </span>
              </div>
              <div className={`flex items-center justify-between text-xs ${variant.totalSubClass}`}>
                <span>≈ Labour on invoice</span>
                <span className="font-mono font-semibold">£{estimatedInvoice.toFixed(2)}</span>
              </div>
            </div>
          )}
        </div>

        <div className="border-t border-slate-100 px-5 py-4 flex justify-end gap-2">
          <button onClick={onClose} className="btn btn-secondary" disabled={saving}>
            Cancel
          </button>
          <button
            onClick={confirm}
            disabled={saving}
            className={`btn ${variant.buttonClass}`}
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Icon className="h-4 w-4" />}
            {variant.confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
